import { useState } from 'react'

import { F0Box, F0Dialog } from '@factorialco/f0-react'
import { ExternalLink } from '@factorialco/f0-react/icons/app'
import { ResourceHeader, Tabs } from '@factorialco/f0-react/dist/experimental'
import { useNavigate } from 'react-router-dom'

import { dateBackendToUI } from 'modules/core/lib/dates'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import i18n from 'modules/core/lib/i18n'
import { paymentStatusVariant, paymentStatusLabel } from 'modules/finance/lib/budgetMovement'

import { CostTab } from './CostTab'
import { ParticipantsTab } from './ParticipantsTab'
import type { TrainingBudgetMovement } from 'modules/finance/components/Budgets/BudgetDetailDataCollection/TrainingResourceDataCollection/index'

const TAB_IDS = { COST: 'cost', PARTICIPANTS: 'participants' } as const

interface Props {
  item: TrainingBudgetMovement
  onClose: () => void
  locale: string
}

export const TrainingGroupDetail = ({ item, onClose, locale }: Props) => {
  const [activeTab, setActiveTab] = useState<string>(TAB_IDS.COST)
  const { navigation } = useNavigation()
  const navigate = useNavigate()

  const resource = item.budgetedResource
  const status = item.status ?? 'draft'
  const timeframe = [
    resource.startDate ? dateBackendToUI(resource.startDate) : null,
    resource.endDate ? dateBackendToUI(resource.endDate) : null,
  ]
    .filter(Boolean)
    .join(' – ')

  const groupUrl =
    navigation.trainings.list.details_revamp.view.trainingClasses.detail.costs.fullPath({
      id: resource.training?.id ?? '0',
      classId: resource.id,
    })

  return (
    <F0Dialog
      isOpen
      onClose={onClose}
      position="right"
      title={i18n.t('budgets.detail_training_budget.sidepanel.title')}
      otherActions={[
        {
          label: i18n.t('budgets.detail_training_budget.sidepanel.go_to_group_costs_page'),
          icon: ExternalLink,
          onClick: () => navigate(groupUrl),
        },
      ]}
    >
      <ResourceHeader
        title={resource.name ?? ''}
        description={resource.training?.name}
        status={{
          label: i18n.t('budgets.detail_training_budget.sidepanel.payment_status_label'),
          text: paymentStatusLabel(status),
          variant: paymentStatusVariant(status),
        }}
        metadata={
          timeframe.length > 0
            ? [
                {
                  label: i18n.t('budgets.detail_training_budget.sidepanel.timeframe_label'),
                  value: { type: 'text' as const, content: timeframe },
                },
              ]
            : []
        }
      />
      <F0Box paddingY="xl">
        <Tabs
          activeTabId={activeTab}
          setActiveTabId={setActiveTab}
          tabs={[
            {
              id: TAB_IDS.COST,
              label: i18n.t('budgets.detail_training_budget.sidepanel.cost_tab'),
            },
            {
              id: TAB_IDS.PARTICIPANTS,
              label: i18n.t('budgets.detail_training_budget.sidepanel.participants_tab'),
            },
          ]}
        />
      </F0Box>

      {activeTab === TAB_IDS.COST && <CostTab item={item} locale={locale} groupUrl={groupUrl} />}

      {activeTab === TAB_IDS.PARTICIPANTS && <ParticipantsTab classId={item.budgetedResource.id} />}
    </F0Dialog>
  )
}
